import { Button, Flex, Text, useToast } from '@chakra-ui/react';
import { EmptyDataButton } from '@texas/components/shared/button/EmptyDataButton';
import { PasteFileInput } from '@texas/components/shared/dropzone/PasteFileInput';
import { notImageValidator } from '@texas/components/shared/dropzone/shared';
import { usePasteInput } from '@texas/components/shared/dropzone/usePasteInput';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { WarningComponent } from '@texas/components/shared/WarningComponent';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

export function LayoutDropzone({
  onUpload,
  onfileModalOpen,
  fileId,
}: {
  onUpload: (files: File[]) => void;
  onfileModalOpen: () => void;
  fileId: number | null;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { inputRef, setFocus, setBlur } = usePasteInput();

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop: handleDrop,
      useFsAccessApi: false,
      noClick: true,
      multiple: false,
      validator: (file) => notImageValidator(file, t),
    });

  function handleDrop(acceptedFiles: File[], fileRejections: FileRejection[]) {
    if (fileRejections.length) {
      toast({
        position: 'bottom',
        title: t('fileBrowser.uploadFailed'),
        description: fileRejections.map((rejection) => (
          <Flex key={rejection.file.path} direction="column">
            <Text fontWeight="medium">{rejection.file.name}</Text>
            {rejection.errors.map((e) => (
              <Text key={e.code}>{e.message}</Text>
            ))}
          </Flex>
        )),
        status: 'error',
        isClosable: true,
      });
    }

    if (acceptedFiles.length) {
      onUpload(acceptedFiles);
    }
  }

  return (
    <Flex
      direction="column"
      w="fit-content"
      onMouseEnter={setFocus}
      onMouseLeave={setBlur}
      {...getRootProps()}
    >
      {fileId ? (
        <Button
          onClick={onfileModalOpen}
          variant="texas-light"
          h="100%"
          w="fit-content"
          leftIcon={
            isDragReject ? (
              <Icons.Cancel
                boxSize={defaultIconSize}
                color="red.300"
                _light={{ color: 'red.500' }}
              />
            ) : isDragActive ? (
              <Icons.UploadBoxOutline boxSize={defaultIconSize} />
            ) : (
              <Icons.PencilRuler />
            )
          }
        >
          {isDragActive
            ? isDragReject
              ? t('fileBrowser.notValid')
              : t('fileBrowser.dropToUpload')
            : t('compositionGroup.option.files.viewLayout')}
        </Button>
      ) : (
        <WarningComponent
          w="200px"
          warning={t('compositionGroup.option.noLayout')}
        >
          <EmptyDataButton
            onClick={onfileModalOpen}
            label={
              isDragActive
                ? isDragReject
                  ? t('fileBrowser.notValid')
                  : t('fileBrowser.dropToUpload')
                : t('compositionGroup.option.files.noLayout')
            }
            description={isDragActive ? '' : t('general.clickToAdd')}
          />
        </WarningComponent>
      )}
      <PasteFileInput
        ref={inputRef}
        onUpload={onUpload}
        validator={(file) => notImageValidator(file, t)}
        multiple={false}
      />
      <input multiple={false} {...getInputProps()} />
    </Flex>
  );
}
